/* eslint-disable @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-explicit-any */
import { createFileRoute, useParams, useNavigate } from '@tanstack/react-router'
import { useState } from 'react'
import { Plus } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog'
import { useServiceCaseDetail } from '@/hooks/useServiceCases'
import { useCreateEstimate } from '@/hooks/useEstimates'
import { useHasPermission } from '@/stores/authStore'
import { useTranslation } from 'react-i18next'

export const Route = createFileRoute('/_authenticated/service-cases/$id/overview')({
  component: CaseOverviewTab,
})

function CaseOverviewTab() {
  const { t } = useTranslation()
  const { id } = useParams({ from: '/_authenticated/service-cases/$id' })
  const navigate = useNavigate()
  const { data: caseData } = useServiceCaseDetail(id)
  const sc = (caseData as any)?.data ?? caseData

  const canCreateEstimate = useHasPermission('estimates.create')
  const canCreateAppointment = useHasPermission('appointments.create')
  const canCreatePartRequest = useHasPermission('part_requests.create')

  const [showCreateEstimate, setShowCreateEstimate] = useState(false)
  const createEstimate = useCreateEstimate()

  if (!sc) return null

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader><CardTitle>{t('caseDetail:caseInfo', 'Case Information')}</CardTitle></CardHeader>
        <CardContent className="text-sm space-y-1">
          <p>Created: {new Date(sc.created_at).toLocaleString()}</p>
          {sc.customer_id && <p>Customer ID: {sc.customer_id}</p>}
        </CardContent>
      </Card>
      <div className="flex gap-2 flex-wrap">
        {canCreateEstimate && (
          <Button size="sm" variant="outline" onClick={() => setShowCreateEstimate(true)}>
            <Plus className="h-3 w-3 mr-1" />{t('caseDetail:createEstimate')}
          </Button>
        )}
        {canCreateAppointment && (
          <Button size="sm" variant="outline" onClick={() => navigate({ to: `/appointments/new?case=${id}` })}>
            <Plus className="h-3 w-3 mr-1" /> Schedule Appointment
          </Button>
        )}
        {canCreatePartRequest && (
          <Button size="sm" variant="outline" onClick={() => navigate({ to: `/part-requests/new?case=${id}` })}>
            <Plus className="h-3 w-3 mr-1" /> Create Part Request
          </Button>
        )}
      </div>

      <Dialog open={showCreateEstimate} onOpenChange={setShowCreateEstimate}>
        <DialogContent>
          <DialogHeader><DialogTitle>{t('caseDetail:createEstimate')}</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">A new estimate will be created and linked to this case.</p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateEstimate(false)}>Cancel</Button>
            <Button
              disabled={createEstimate.isPending}
              onClick={() => {
                createEstimate.mutate(
                  { service_case_id: id, customer_id: sc.customer_id } as any,
                  { onSuccess: (est: any) => { setShowCreateEstimate(false); navigate({ to: `/estimates/${est.id ?? est.data?.id}` }); } },
                )
              }}
            >
              {createEstimate.isPending ? 'Creating...' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
